// Converts an ingredient amount between US and metric measures, using the
// rounded kitchen equivalents cooks actually use (1 cup = 240 ml, 1 tbsp =
// 15 ml) rather than exact lab figures. Returns { amount, unit } in the target
// system, or null when there's nothing to convert: the unit is already in that
// system, isn't a measure ("cloves", "unit"), the recipe is shown as written,
// or it's a pinch that would round to zero in the other system.

const VOLUME_ML = {
  tsp: 5, tsps: 5, teaspoon: 5, teaspoons: 5,
  tbsp: 15, tbsps: 15, tablespoon: 15, tablespoons: 15,
  cup: 240, cups: 240,
  "fl oz": 30, "fluid ounce": 30, "fluid ounces": 30,
  pint: 480, pints: 480, quart: 960, quarts: 960, gallon: 3840, gallons: 3840,
  ml: 1, milliliter: 1, milliliters: 1, millilitre: 1, millilitres: 1,
  l: 1000, liter: 1000, liters: 1000, litre: 1000, litres: 1000
}

const WEIGHT_G = {
  oz: 28.35, ounce: 28.35, ounces: 28.35,
  lb: 453.6, lbs: 453.6, pound: 453.6, pounds: 453.6,
  g: 1, gram: 1, grams: 1,
  kg: 1000, kilogram: 1000, kilograms: 1000
}

const METRIC = new Set(["ml", "milliliter", "milliliters", "millilitre", "millilitres", "l", "liter", "liters", "litre", "litres", "g", "gram", "grams", "kg", "kilogram", "kilograms"])

export function convertAmount(amount, unit, system) {
  const key = String(unit || "").trim().toLowerCase().replace(/\.$/, "")
  const isVolume = key in VOLUME_ML
  const isWeight = key in WEIGHT_G
  if (system === "original" || !(isVolume || isWeight) || !Number.isFinite(amount)) return null
  if ((system === "metric") === METRIC.has(key)) return null

  const converted = system === "metric"
    ? (isVolume ? toMetricVolume(amount * VOLUME_ML[key]) : toMetricWeight(amount * WEIGHT_G[key]))
    : (isVolume ? toUsVolume(amount * VOLUME_ML[key]) : toUsWeight(amount * WEIGHT_G[key]))
  return converted.amount > 0 ? converted : null
}

function toMetricVolume(ml) {
  if (ml >= 1000) return { amount: round(ml / 1000, 0.1), unit: "l" }
  return { amount: ml >= 10 ? round(ml, 5) : round(ml, 0.5), unit: "ml" }
}

function toMetricWeight(g) {
  if (g >= 1000) return { amount: round(g / 1000, 0.1), unit: "kg" }
  return { amount: g >= 20 ? round(g, 5) : Math.max(1, Math.round(g)), unit: "g" }
}

function toUsVolume(ml) {
  if (ml < 15) return { amount: round(ml / 5, 0.25), unit: "tsp" }
  if (ml < 60) return { amount: round(ml / 15, 0.5), unit: "tbsp" }
  const cups = round(ml / 240, 0.25)
  return { amount: cups, unit: cups > 1 ? "cups" : "cup" }
}

function toUsWeight(g) {
  if (g < 453.6) return { amount: round(g / 28.35, 0.5), unit: "oz" }
  return { amount: round(g / 453.6, 0.1), unit: "lb" }
}

function round(n, step) {
  return Math.round(Math.round(n / step) * step * 1000) / 1000
}
